// src/helpers/useHoldingsHistoryHelper.ts

import { QoqPositionDto } from "../api/dtos";
import { normalizeType, parseYearQuarter } from "../helpers/helpers";
import { PositionData, QuarterData } from "../components/13FQoqHistory/types";

/* Period helpers */
export const toFromPeriod = (y: number) => `${y}Q1`;
export const toToPeriod   = (y: number) => `${y}Q4`;

/* Ticker sanitizing */
export const TICKER_RE = /^[A-Z0-9.-]{1,10}$/;
export const sanitizeTicker = (s: string) =>
    (s || "")
        .toUpperCase()
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[–—]/g, "-")
        .trim();

export const isValidTicker = (t: string) => TICKER_RE.test(t);

/* Map API dto -> PositionData (canonical field names used in UI) */
export const mapQoq = (i: QoqPositionDto): PositionData => ({
    ...i,
    // normalize backend enum "positionTypeEnum" -> positionType
    positionType: normalizeType(i.positionTypeEnum),

    // QoQ helpers
    isNewPosition: i.isNew,
    isPositionExited: i.isExited,

    qoqValueChange: i.valueDelta,
    qoqValueChangePercent: i.valueDeltaPercent,
    qoqAmountChange: i.amountDelta,
    qoqAmountChangePercent: i.amountDeltaPercent,

    // carry weight through to UI (share of portfolio)
    weight: i.weight,
});

/* Filters / mapping */
export function filterAndMapFactory(showPositionTypes: Record<string, boolean>) {
    return (items?: QoqPositionDto[]) =>
        (items ?? [])
            .filter((r) => !!r?.period)
            .filter((r) => showPositionTypes[normalizeType(r.positionTypeEnum)])
            .map(mapQoq);
}

/* QuarterData helpers */
export function appendGroupedByPeriod(prev: QuarterData[], items: PositionData[]): QuarterData[] {
    if (!items?.length) return prev;

    const by = new Map<string, PositionData[]>();
    for (const p of items) {
        const list = by.get(p.period) ?? [];
        list.push(p);
        by.set(p.period, list);
    }

    const next = [...prev];
    for (const [period, list] of by) {
        const year = Number(period.slice(0, 4));
        const quarter = `Q${period.slice(5)}`;
        const idx = next.findIndex((q) => q.period === period);
        if (idx === -1) {
            next.push({ year, quarter, period, positions: list });
        } else {
            next[idx] = {
                ...next[idx],
                positions: [...next[idx].positions, ...list],
            };
        }
    }

    // newest period first, i.e. "2025Q2" before "2025Q1"
    next.sort((a, b) => (a.period < b.period ? 1 : -1));
    return next;
}

export function computeAllYearQuarters(
    data: QuarterData[],
    fromYear: number,
    toYear: number
): string[] {
    const set = new Set<string>();
    data.forEach((qd) => {
        if ((qd.positions?.length ?? 0) > 0) {
            set.add(`${qd.year}-Q${qd.quarter.slice(1)}`);
        }
    });

    const arr = Array.from(set).filter((yq) => {
        const { year } = parseYearQuarter(yq);
        return year >= fromYear && year <= toYear;
    });

    arr.sort((a, b) => {
        const { year: ya, quarterNumber: qa } = parseYearQuarter(a);
        const { year: yb, quarterNumber: qb } = parseYearQuarter(b);
        if (yb !== ya) return yb - ya;
        return qb - qa;
    });
    return arr;
}

/* ------------ Pivots (institution mode) ------------ */
export function buildPivotInstitutionMode(
    data: QuarterData[]
): Record<string, Record<string, Record<string, PositionData>>> {
    const pivot: Record<string, Record<string, Record<string, PositionData>>> = {};

    for (const qd of data) {
        const yq = `${qd.year}-Q${qd.quarter.slice(1)}`; // "2025-Q2"
        for (const p of qd.positions) {
            const type = normalizeType(p.positionType);
            const t = (p.ticker || "").trim();
            if (!t) continue;

            (pivot[t] ??= {});
            (pivot[t][type] ??= {});
            pivot[t][type][yq] = p;
        }
    }

    return pivot;
}

/* ------------ Ticker mode row keys ------------ */
export type RowKey = string; // `${ticker}|${normalizeType(type)}|${institutionId}`

export const makeRowKey = (t: string, type: string, inst?: number | null) =>
    `${(t || "").trim()}|${normalizeType(type)}|${inst ?? 0}`;

export function parseRowKey(rowKey: RowKey) {
    const [ticker, type, instStr] = rowKey.split("|");
    return {
        ticker,
        type,
        institutionId: Number.isFinite(Number(instStr)) ? Number(instStr) : 0,
    };
}

/* Build row-pivot for ticker mode: rowKey -> { yq: PositionData } */
export function buildRowPivot(data: QuarterData[]) {
    const pivot: Record<RowKey, Record<string, PositionData>> = {};

    for (const qd of data) {
        const yq = `${qd.year}-Q${qd.quarter.slice(1)}`;
        for (const p of qd.positions) {
            const t = (p.ticker || "").trim();
            if (!t) continue;

            const key = makeRowKey(t, String(p.positionType), p.institutionId);
            (pivot[key] ??= {});
            pivot[key][yq] = p;
        }
    }

    return pivot;
}

/* For iteration/sorting in UI tables */
export interface UIPair {
    ticker: string;
    type: string;
    institutionId: number | null | undefined;
    rowKey: RowKey;
}

export function makePairsTickerMode(
    pivot: Record<RowKey, Record<string, PositionData>>
): UIPair[] {
    const arr: UIPair[] = [];
    Object.keys(pivot).forEach((rowKey) => {
        const { ticker, type, institutionId } = parseRowKey(rowKey);
        arr.push({ rowKey, ticker, type, institutionId });
    });
    return arr;
}

export function sortPairsByNewestValueTicker(
    pairs: UIPair[],
    pivot: Record<RowKey, Record<string, PositionData>>,
    yq?: string | null
) {
    if (!yq) return pairs;
    const val = (a: UIPair) => pivot[a.rowKey]?.[yq]?.value ?? 0;
    return [...pairs].sort((a, b) => val(b) - val(a));
}

export function totalValueAtTicker(
    pairs: UIPair[],
    pivot: Record<RowKey, Record<string, PositionData>>,
    yq?: string | null
) {
    if (!yq) return 0;
    return pairs.reduce((s, p) => s + (pivot[p.rowKey]?.[yq]?.value ?? 0), 0);
}

/* ------------ Aggregated ticker mode ------------ */
export function buildAggregatedTickerPivot(
    pivot: Record<RowKey, Record<string, PositionData>>
) {
    const groups = new Map<string, RowKey[]>();
    Object.keys(pivot).forEach((rk) => {
        const { ticker, type } = parseRowKey(rk);
        const k = `${ticker}|${type}`;
        const arr = groups.get(k) ?? [];
        arr.push(rk);
        groups.set(k, arr);
    });

    const agg: Record<RowKey, Record<string, PositionData>> = {};

    for (const [k, rowKeys] of groups) {
        const [ticker, type] = k.split("|");
        const outKey = makeRowKey(ticker, type, 0);

        const yqs = new Set<string>();
        rowKeys.forEach((rk) => {
            Object.keys(pivot[rk]).forEach((yq) => yqs.add(yq));
        });

        const outRow: Record<string, PositionData> = {};

        for (const yq of Array.from(yqs)) {
            let value = 0;
            let amount = 0;

            let vDeltaSum = 0;
            let aDeltaSum = 0;
            let sawV = false;
            let sawA = false;

            let sample: PositionData | undefined;

            for (const rk of rowKeys) {
                const p = pivot[rk][yq];
                if (!p) continue;
                sample = sample ?? p;

                value += Number(p.value ?? 0);
                amount += Number(p.amount ?? 0);

                const vd = typeof p.qoqValueChange === "number" ? p.qoqValueChange : p.valueDelta;
                vDeltaSum += vd;
                sawV = true;

                const ad = typeof p.qoqAmountChange === "number" ? p.qoqAmountChange : p.amountDelta;
                aDeltaSum += ad;
                sawA = true;
            }

            const base: PositionData = {
                ...(sample || ({} as any)),
                ticker,
                positionType: normalizeType(type),
                value,
                amount,
                // weight not well-defined across many institutions -> leave undefined (UI will fallback)
                weight: undefined,
            } as any;

            if (sawV) base.qoqValueChange = vDeltaSum;
            if (sawA) base.qoqAmountChange = aDeltaSum;

            outRow[yq] = base;
        }

        const sortedYQs = Object.keys(outRow).sort((a, b) => (a < b ? 1 : -1));
        for (let i = 0; i < sortedYQs.length; i++) {
            const yq = sortedYQs[i];
            const prev = sortedYQs[i + 1] ? outRow[sortedYQs[i + 1]] : undefined;
            const cur = outRow[yq];

            if (typeof cur.qoqValueChange === "number" && prev?.value) {
                cur.qoqValueChangePercent = (cur.qoqValueChange / Math.abs(prev.value)) * 100;
            }
            if (typeof cur.qoqAmountChange === "number" && prev?.amount) {
                cur.qoqAmountChangePercent = (cur.qoqAmountChange / Math.abs(prev.amount)) * 100;
            }
        }

        agg[outKey] = outRow;
    }

    const aggPairs = Object.keys(agg).map((rowKey) => {
        const { ticker, type } = parseRowKey(rowKey);
        return { rowKey, ticker, type, institutionId: 0 };
    });

    return { aggPivot: agg, aggPairs };
}
